import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useThemeStore from './stores/useThemeStore';
import { useMuiBrandTheme } from './theme/muiTheme';
import useAuthStore from './stores/useAuthStore';
import { keepSessionFresh } from 'Api/portalClient';
import { size } from 'theme/tokens';
import AppLayout from './components/layout/AppLayout';
import { routes, LoginPage, ChangePasswordPage } from './config/routes';
import AccessDeniedPage from './pages/AccessDeniedPage';
import PartnerSpinner from './components/common/PartnerSpinner';
// Eager on purpose: it has to render while offline, when no chunk can load.
import NoNetworkGuard from 'components/noNetwork/NoNetworkGuard';
import { PARTNER_PERMISSIONS, assertNavPermissionCoverage, canSeePath } from 'constants/navPermissions';
import { RBAC_NAV_ENABLED, firstVisiblePath } from 'helpers/navVisibility';
import sidebarNav from './config/navigation';
import { useListFilterReset } from 'helpers/listFilterMemory';
import './partnerPolish.css';

/**
 * Warn, in development, about partner routes nobody mapped to a permission.
 * See the admin twin in AdminApp.jsx for why this exists rather than being left
 * to the fail-open default.
 *
 * The partner route table stores paths relative to the `/partnerAdmin` mount
 * (`'/'`, `'/bookings'`), so the prefix is reattached here; `'/'` is the
 * dashboard and maps to the bare `/partnerAdmin`.
 */
assertNavPermissionCoverage(
  routes.map((route) => (route.path === '/' ? '/partnerAdmin' : `/partnerAdmin${route.path}`)),
  PARTNER_PERMISSIONS,
  'partner admin',
);

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: size[100] }}>
    <PartnerSpinner />
  </div>
);

/**
 * Signed in, and past the forced password rotation.
 *
 * The `mustChangePassword` redirect is what makes that rotation mandatory rather
 * than a suggestion: sign-in routes a first-login user to the change-password
 * screen, but nothing stopped them typing /partnerAdmin and entering the console
 * on the temporary password from their invite email — which is exactly the
 * credential the rotation exists to retire.
 */
function ProtectedRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  if (!isAuthenticated) return <Navigate to="/partnerAdmin/login" replace />;
  if (mustChangePassword) return <Navigate to="/partnerAdmin/change-password" replace />;
  return children;
}

/** Authenticated, but deliberately NOT behind the rotation gate above. */
function RotationRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/partnerAdmin/login" replace />;
  return children;
}

/**
 * Signed in, and granted the module this screen belongs to. The partner twin of
 * AdminApp's `PermissionRoute` — same table, same fail-open rule, same reason for
 * rendering the 403 in place instead of redirecting. That file carries the full
 * reasoning; the important half of it is that this is AFFORDANCE, not
 * enforcement: `permissions` is client-side data, and app-backend's route
 * authorization is the actual boundary.
 */
function PermissionRoute({ children }) {
  const perms = useAuthStore((s) => s.perms);
  const { pathname } = useLocation();

  if (!RBAC_NAV_ENABLED) return children;
  if (canSeePath(perms, pathname)) return children;

  // Same dashboard-is-gated problem as the admin twin, and the partner portal
  // has it twice over: its catch-all sends every unmatched URL here too.
  if (pathname === '/partnerAdmin' || pathname === '/partnerAdmin/') {
    const fallback = firstVisiblePath(sidebarNav, perms);
    if (fallback) return <Navigate to={fallback} replace />;
  }
  return <AccessDeniedPage />;
}

export default function PartnerApp() {
  // List filters survive a detail-page round trip; a sidebar menu clears them.
  useListFilterReset(sidebarNav);

  /**
   * Rotate the access token on a TIMER while signed in.
   *
   * The partner half of the same hole the CRM's AuthContext closes: every other
   * refresh in portalClient is lazy (it happens because the portal is about to
   * make a call), which never fires on `/partnerAdmin/travel/*` because the
   * operator is working inside the embedded traveller app — whose requests go
   * straight to app-backend and never pass through `postToBackend` — while the
   * portal itself issues nothing. The token lapsed with nothing to renew it and
   * every call from the frame answered FAST_JWT_EXPIRED.
   *
   * Lives here rather than in `useAuthStore` because the store is not a
   * component and a module-level timer would start before there is a session to
   * keep. Keyed on WHETHER there is a session, not on the token: the loop
   * re-reads the stored expiry each tick, so a rotation must not restart it.
   */
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return keepSessionFresh('partner');
  }, [isAuthenticated]);

  /**
   * Load this partner's branding — §30.1, ONCE per tab.
   *
   * Gated on a session because the request is keyed by `partnerCode`, which only
   * exists after sign-in. Deliberately NOT awaited and deliberately not tied to
   * a loading state: the shell has already painted from the cached brand (or the
   * default) before this runs, so the call is pure refresh and must not hold up
   * a single pixel. `hydrateBranding` de-duplicates, so StrictMode's double
   * mount and a re-login in the same tab both collapse to one request, and a
   * failure leaves the console on the colours already shown.
   */
  const muiTheme = useMuiBrandTheme();
  const hydrateBranding = useThemeStore((s) => s.hydrateBranding);
  useEffect(() => {
    if (!isAuthenticated) return;
    hydrateBranding();
  }, [isAuthenticated, hydrateBranding]);

  /**
   * The browser-tab title. `public/index.html` ships "Zoya - Admin-app" for the
   * whole bundle, which named the CRM on a partner's own console. Signed in, the
   * tab carries the partner's name; on the login screen there is no partner yet.
   * The shipped title is put back on unmount, since the CRM shares this bundle.
   */
  const partnerName = useAuthStore((s) => s.partner?.name);
  useEffect(() => {
    const shipped = document.title;
    document.title = partnerName ? `${partnerName} | Partner Portal` : 'Partner Portal';
    return () => { document.title = shipped; };
  }, [partnerName]);

  return (
    <div id="partner-admin-root">
      {/*
        ONE MUI theme for the whole portal, fed from the partner's resolved
        branding (partner/theme/muiTheme.js), so every screen is branded by
        default and a new one needs no wiring. It cannot reach the admin
        console — it has its own provider — nor the traveller-app frame, which
        is cross-origin.
      */}
      <MuiThemeProvider theme={muiTheme}>
        <Routes>
          <Route
            path="login"
            element={
              <Suspense fallback={<Loading />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="change-password"
            element={
              <RotationRoute>
                <Suspense fallback={<Loading />}>
                  <ChangePasswordPage />
                </Suspense>
              </RotationRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={<PermissionRoute>{route.element}</PermissionRoute>}
              />
            ))}
          </Route>
          {/*
            Catch-all. Without it an unmatched /partnerAdmin/* path rendered a
            blank white page with no error — which is how the missing
            change-password route stayed invisible.
          */}
          <Route path="*" element={<Navigate to="/partnerAdmin" replace />} />
        </Routes>
        {/* Inside the brand theme (so the page paints the partner's
            primary), outside the auth gate (covers login too), and beside
            the routes rather than around them: it overlays the console, so
            nothing underneath is unmounted by an outage. */}
        <NoNetworkGuard />
      </MuiThemeProvider>
    </div>
  );
}
